class InfiniteCarouselComponent extends HTMLElement {
  /** @type {Object.<string, HTMLElement | HTMLElement[]>} */
  refs = {};
  /** @type {any[]} */
  #data = []; 
  /** @type {HTMLElement[]} */
  #pool = []; 
  /** @type {number} */
  #offset = 0; 
  /** @type {number} */
  #velocity = 0; 
  /** @type {boolean} */
  #isDragging = false; 
  /** @type {boolean} */
  #isLocked = false;
  /** @type {boolean} */
  #isDown = false;
  #startX = 0; #startY = 0; #lastX = 0; #startOff = 0; #cardW = 0; #gap = 0; 
  /** @type {number} */
  #raf = 0;

  constructor() {
    super();
    this._moveHandler = this.#move.bind(this);
    this._upHandler = this.#up.bind(this);
    this._touchMoveHandler = this.#handleTouchMove.bind(this);
    this._wheelHandler = this.#handleWheel.bind(this);
  }

  connectedCallback() {
    this.refs = {
      viewport: this.querySelector('[ref="viewport"]'),
      track: this.querySelector('[ref="track"]')
    };

    const sc = this.querySelector('script[type="application/json"]');
    if (!sc) return;
    try { this.#data = JSON.parse(sc.textContent || "[]"); } catch(e) { return; }
    
    if (this.#data.length === 0) {
      this.style.display = 'none';
      return;
    }

    this.#pool = Array.from(this.querySelectorAll('.infinite-carousel__card'));
    const v = /** @type {HTMLElement} */ (this.refs.viewport);
    if (!v) return;
    
    v.addEventListener('mousedown', (e) => this.#handleMouseDown(e));
    v.addEventListener('touchstart', (e) => this.#handleTouchStart(e), { passive: true });
    v.addEventListener('wheel', this._wheelHandler, { passive: false });
    v.addEventListener('click', (e) => Math.abs(this.#offset - this.#startOff) > 10 && (e.preventDefault(), e.stopPropagation()), true);
    v.addEventListener('dragstart', (e) => e.preventDefault());

    window.addEventListener('mousemove', this._moveHandler);
    window.addEventListener('mouseup', this._upHandler);
    window.addEventListener('touchmove', this._touchMoveHandler, { passive: false });
    window.addEventListener('touchend', this._upHandler);

    this.observer = new ResizeObserver(() => this.#init());
    this.observer.observe(v);
    
    this.#init();
    this.#raf = requestAnimationFrame(this.#onFrame.bind(this));
  }

  disconnectedCallback() { 
    cancelAnimationFrame(this.#raf);
    this.observer?.disconnect();
    window.removeEventListener('mousemove', this._moveHandler);
    window.removeEventListener('mouseup', this._upHandler);
    window.removeEventListener('touchmove', this._touchMoveHandler);
    window.removeEventListener('touchend', this._upHandler);
  }

  #handleMouseDown(e) {
    this.#isDown = true;
    this.#down(e);
  }

  #handleTouchStart(e) {
    if (!e.touches[0]) return;
    this.#isDown = true;
    this.#isDragging = false;
    this.#isLocked = false;
    this.#down(e.touches[0]);
    this.#startY = e.touches[0].clientY;
  }

  #handleTouchMove(e) {
    if (!this.#isDown || !e.touches[0]) return;
    const t = e.touches[0], dx = Math.abs(t.clientX - this.#startX), dy = Math.abs(t.clientY - this.#startY);
    
    if (!this.#isLocked) { 
        if (dx > 10 && dx > dy) { 
          this.#isLocked = true; 
          this.#isDragging = true;
        } 
        else if (dy > 10) {
          this.#isDragging = false;
          this.#isDown = false;
        }
    }
    
    if (this.#isLocked) { 
        if (e.cancelable) e.preventDefault(); 
        this.#move(t); 
    }
  }

  #handleWheel(e) {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) { 
        e.preventDefault(); 
        this.#velocity = -e.deltaX * 0.5; 
        this.#isDragging = false; 
    } 
  }

  #down(/** @type {MouseEvent | Touch} */ e) { 
    this.#isDragging = true; 
    this.#startX = this.#lastX = e.clientX; 
    this.#startOff = this.#offset; 
    this.#velocity = 0; 
    const v = /** @type {HTMLElement} */ (this.refs.viewport);
    if(v) v.style.cursor = 'grabbing'; 
  }

  #move(/** @type {MouseEvent | Touch} */ e) { 
    if (!this.#isDragging || !this.#isDown) return; 
    this.#offset = this.#startOff + (e.clientX - this.#startX); 
    this.#velocity = e.clientX - this.#lastX; 
    this.#lastX = e.clientX; 
  }

  #up() { 
    this.#isDragging = this.#isLocked = this.#isDown = false; 
    const v = /** @type {HTMLElement} */ (this.refs.viewport);
    if(v) v.style.cursor = 'grab'; 
  }

  #vW = 0;

  #init() {
    const v = /** @type {HTMLElement} */ (this.refs.viewport);
    const t = /** @type {HTMLElement} */ (this.refs.track);
    if (!v || !this.#pool.length) return;
    this.#vW = v.offsetWidth || window.innerWidth;
    this.#cardW = this.#pool[0].offsetWidth || this.#vW * 0.63;
    if (this.#cardW <= 0) this.#cardW = this.#vW * 0.63;

    const gStr = window.getComputedStyle(t).gap;
    this.#gap = (gStr && gStr !== 'normal') ? parseInt(gStr) : (this.#vW < 750 ? 18 : 32);
    if (isNaN(this.#gap)) this.#gap = 18;

    if (!this.#offset) this.#offset = (this.#vW - this.#cardW) / 2;
    
    this.#pool.forEach(c => {
      Object.assign(c.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        width: `${this.#cardW}px`,
        height: '100%',
        display: 'flex'
      });
    });
  }

  #onFrame() {
    const v = /** @type {HTMLElement} */ (this.refs.viewport);
    const t = /** @type {HTMLElement} */ (this.refs.track);
    if (!v || !t || this.#data.length === 0) return;

    const step = this.#cardW + this.#gap;
    if (!this.#isDragging) {
      this.#offset += this.#velocity; this.#velocity *= 0.95;
      if (Math.abs(this.#velocity) < 1.5) {
        const center = (this.#vW - this.#cardW) / 2;
        const target = center + Math.round((this.#offset - center) / step) * step;
        this.#offset += (target - this.#offset) * 0.12;
      }
    }
    
    t.style.transform = `translate3d(${this.#offset}px,0,0)`;
    
    const centerPos = (this.#vW - this.#cardW) / 2;
    const base = -Math.round((this.#offset - centerPos) / step);
    
    this.#pool.forEach((c, i) => {
      const lIdx = base + (i - 4);
      const dIdx = ((lIdx % this.#data.length) + this.#data.length) % this.#data.length;
      const item = this.#data[dIdx];
      
      if (!item) {
        if (c.style.display !== 'none') c.style.display = 'none';
        return;
      }
      if (c.style.display === 'none') c.style.display = 'flex';
      
      const logicalLeft = lIdx * step;
      const cardCenterInViewport = logicalLeft + this.#offset + this.#cardW/2;
      const distFromCenter = Math.abs(cardCenterInViewport - this.#vW/2);
      
      const tt = Math.max(0, Math.min(1, distFromCenter / (step * 1.5)));
      const scale = 1 - (tt * 0.15);
      
      let visualPos = logicalLeft;
      const shrinkage = (1 - scale) * this.#cardW;
      const direction = lIdx > base ? 1 : (lIdx < base ? -1 : 0);
      const absDiff = Math.abs(lIdx - base);
      visualPos -= (direction * shrinkage * 0.5); 
      if (absDiff > 1) {
          visualPos -= direction * (absDiff - 1) * (0.15 * this.#cardW * 0.5);
      }

      // CLS FIX: 'left' (layout property) ki jagah 'transform' (compositor-only) use kiya —
      // visually 100% same position, lekin ab Layout Shift trigger nahi hoga.
      const posStr = `${visualPos}`;
      if (c.dataset.vpos !== posStr) {
        c.style.transform = `translate3d(${visualPos}px, 0, 0)`;
        c.dataset.vpos = posStr;
      }
      
      const zIndex = distFromCenter < step/2 ? '10' : '1';
      if (c.style.zIndex !== zIndex) c.style.zIndex = zIndex;

      const link = /** @type {HTMLElement} */ (c.querySelector(".infinite-carousel__link"));
      if (link) {
          link.style.transform = `scale(${scale})`;
          link.style.width = `${this.#cardW}px`;
      }

      // Update Data
      if (c.dataset.id !== item.id) {
        const img = /** @type {HTMLImageElement} */ (c.querySelector("img"));
        const a = /** @type {HTMLAnchorElement} */ (c.querySelector("a"));
        if (img) {
          // CHANGED: viewport width ke hisaab se sahi size image select karo (mobile pe chhoti, desktop pe badi)
          img.src = this.#vW < 750 ? (item.image_mobile || item.image) : (item.image_desktop || item.image);
          img.alt = item.alt;
        }
        if (a) a.href = item.link;
        // ===== TITLE & SUBTITLE =====
        const titleEl = c.querySelector('.infinite-carousel__card-title');
        const subtitleEl = c.querySelector('.infinite-carousel__card-subtitle');
        if (titleEl) titleEl.textContent = item.title || '';
        if (subtitleEl) subtitleEl.textContent = item.subtitle || '';
        // ============================
        c.dataset.id = item.id;
      }
    });
    this.#raf = requestAnimationFrame(this.#onFrame.bind(this));
  }
}
customElements.get("infinite-carousel-component") || customElements.define("infinite-carousel-component", InfiniteCarouselComponent);